/**
 * swipeBack.js
 * Implementa il gesto swipe iOS-style per navigare indietro o aprire il menu.
 * 
 * Comportamento:
 * - Nella home page (/): swipe da sinistra apre il menu hamburger
 * - In altre pagine: swipe da sinistra torna indietro (router.back())
 * 
 * Questo modulo emette un evento custom 'swipe-open-menu' che il layout può ascoltare.
 */

const SWIPE_THRESHOLD = 80 // distanza minima in px per attivare l'azione
const EDGE_ZONE = 40 // larghezza della zona del bordo sinistro che attiva lo swipe

let touchStartX = 0
let touchStartY = 0
let touchStartTime = 0
let isSwipeActive = false

// Elemento per il feedback visivo
let swipeIndicator = null

/**
 * Inizializza il listener di swipe-back.
 * Da chiamare in App.vue onMounted solo su mobile nativo.
 * 
 * @param {object} router - Vue Router instance
 */
export function initSwipeBack(router) {
  if (!window?.Capacitor?.isNativePlatform?.()) {
    // Solo su mobile nativo (iOS/Android)
    return
  }

  const handleTouchStart = (e) => {
    // Controlla se il tocco inizia nella zona del bordo sinistro
    const touch = e.touches[0]
    if (touch.clientX <= EDGE_ZONE) {
      touchStartX = touch.clientX
      touchStartY = touch.clientY
      touchStartTime = Date.now()
      isSwipeActive = true
      
      // Crea l'indicatore visivo se non esiste
      if (!swipeIndicator) {
        createSwipeIndicator()
      }
    }
  }

  const handleTouchMove = (e) => {
    if (!isSwipeActive) return

    const touch = e.touches[0]
    const deltaX = touch.clientX - touchStartX
    const deltaY = touch.clientY - touchStartY

    // Se lo swipe è più verticale che orizzontale, annulla
    if (Math.abs(deltaY) > Math.abs(deltaX)) {
      isSwipeActive = false
      hideSwipeIndicator()
      return
    }

    // Aggiorna l'indicatore visivo
    if (deltaX > 0) {
      updateSwipeIndicator(deltaX, touch.clientY)
    }

    // Previeni scroll durante swipe orizzontale
    if (deltaX > 20) {
      e.preventDefault()
    }
  }

  const handleTouchEnd = (e) => {
    if (!isSwipeActive) return

    const touch = e.changedTouches[0]
    const deltaX = touch.clientX - touchStartX
    const deltaY = touch.clientY - touchStartY
    const deltaTime = Date.now() - touchStartTime

    // Swipe verso destra (deltaX positivo), abbastanza lungo, non troppo lento
    if (
      deltaX > SWIPE_THRESHOLD &&
      Math.abs(deltaY) < Math.abs(deltaX) &&
      deltaTime < 500
    ) {
      // Se siamo nella home page, apri il menu hamburger
      const currentPath = router.currentRoute.value.path
      if (currentPath === '/' || currentPath === '') {
        // Emetti evento custom per aprire il menu
        window.dispatchEvent(new CustomEvent('swipe-open-menu'))
        console.log('SwipeBack: apertura menu hamburger (home page)')
      } else {
        // Altrimenti naviga indietro se possibile
        if (window.history.length > 1) {
          router.back()
          console.log('SwipeBack: navigazione indietro')
        }
      }
    }

    isSwipeActive = false
    hideSwipeIndicator()
  }

  // Aggiungi listener al document (passive: false per preventDefault)
  document.addEventListener('touchstart', handleTouchStart, { passive: true })
  document.addEventListener('touchmove', handleTouchMove, { passive: false })
  document.addEventListener('touchend', handleTouchEnd, { passive: true })

  console.log('SwipeBack: gesture attivo (swipe da sinistra: menu in home, back nelle altre pagine)')

  // Ritorna una funzione di cleanup (se necessario rimuovere i listener)
  return () => {
    document.removeEventListener('touchstart', handleTouchStart)
    document.removeEventListener('touchmove', handleTouchMove)
    document.removeEventListener('touchend', handleTouchEnd)
    if (swipeIndicator && swipeIndicator.parentNode) {
      swipeIndicator.parentNode.removeChild(swipeIndicator)
      swipeIndicator = null
    }
  }
}

/**
 * Crea l'indicatore visivo per lo swipe
 */
function createSwipeIndicator() {
  swipeIndicator = document.createElement('div')
  swipeIndicator.className = 'swipe-back-indicator'
  swipeIndicator.innerHTML = `
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <polyline points="15 18 9 12 15 6"></polyline>
    </svg>
  `
  
  // Stili inline per l'indicatore
  Object.assign(swipeIndicator.style, {
    position: 'fixed',
    left: '0',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '60px',
    height: '60px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0, 0, 0, 0.1)',
    borderRadius: '0 50% 50% 0',
    color: '#333',
    opacity: '0',
    transition: 'opacity 0.15s ease-out',
    pointerEvents: 'none',
    zIndex: '9998'
  })
  
  document.body.appendChild(swipeIndicator)
}

/**
 * Aggiorna la posizione e opacità dell'indicatore durante lo swipe
 */
function updateSwipeIndicator(deltaX, touchY) {
  if (!swipeIndicator) return
  
  // Calcola opacità in base alla distanza (max a SWIPE_THRESHOLD)
  const opacity = Math.min(deltaX / SWIPE_THRESHOLD, 1) * 0.8
  
  // Aggiorna stile
  swipeIndicator.style.opacity = opacity
  swipeIndicator.style.top = `${touchY}px`
  swipeIndicator.style.transform = `translateX(${Math.min(deltaX * 0.5, 30)}px) translateY(-50%)`
}

/**
 * Nasconde l'indicatore visivo
 */
function hideSwipeIndicator() {
  if (!swipeIndicator) return
  
  swipeIndicator.style.opacity = '0'
  swipeIndicator.style.transform = 'translateX(0) translateY(-50%)'
}
